/**
 * Versicherte Gefahren — Matrix für Inhaltsversicherung &
 * Ertragsausfallversicherung. Datenbasis: `for-ai/Maske Inhalt hochsummig.xlsx`
 * (Sheet „Sach-Inhalt").
 */

export type GefahrRow = {
  /** Eindeutiger Schlüssel — wird in der Answers-Map verwendet. */
  key: string;
  /** Ziffer in der Excel-Vorlage (für Anzeige). */
  ziffer: string;
  /** Bezeichnung der versicherten Gefahr (mehrzeilig möglich). */
  label: string;
  /** Paragraph aus den VSG (optional, nur zur Info-Anzeige). */
  paragraph?: string;
  /** Aus der Excel: gibt es eine sinnvolle Ertragsausfall-Kopplung? */
  mitErtragsausfall: boolean;
};

export const INHALT_GEFAHREN: ReadonlyArray<GefahrRow> = [
  {
    key: "feuer",
    ziffer: "1",
    label: "Feuer",
    paragraph: "§ B4 Nr. 1 a VSG",
    mitErtragsausfall: true,
  },
  {
    key: "einbruchVandalismus",
    ziffer: "2",
    label: "Einbruchdiebstahl / Vandalismus",
    paragraph: "§ B4 Nr. 1 b aa, bb u. cc VSG",
    mitErtragsausfall: true,
  },
  {
    key: "leitungswasser",
    ziffer: "3",
    label: "Leitungswasser",
    paragraph: "§ B4 Nr. 1 c VSG",
    mitErtragsausfall: true,
  },
  {
    key: "sturmHagel",
    ziffer: "4",
    label: "Sturm / Hagel",
    paragraph: "§ B4 Nr. 1 d VSG",
    mitErtragsausfall: true,
  },
  {
    key: "weitereElementar",
    ziffer: "5",
    label: "Weitere Elementargefahren",
    paragraph: "§ B4 Nr. 1 e VSG",
    mitErtragsausfall: true,
  },
  {
    key: "innereUnruhen",
    ziffer: "6",
    label: "Innere Unruhen, Böswillige Beschädigung, Streik und Aussperrung",
    paragraph: "§ B4 Nr. 1 f VSG",
    mitErtragsausfall: true,
  },
  {
    key: "fahrzeuganprallRauch",
    ziffer: "7",
    label: "Fahrzeuganprall, Rauch und Überschalldruckwellen",
    paragraph: "§ B4 Nr. 1 g VSG",
    mitErtragsausfall: true,
  },
  {
    key: "tvElektronik",
    ziffer: "8a",
    label: "Gefahren der Technischen Versicherung Elektronik (Teildeckung)",
    mitErtragsausfall: false,
  },
  {
    key: "tvMaschinen",
    ziffer: "8b",
    label: "Gefahren der Technischen Versicherung einschließlich Maschinen (Volldeckung)",
    paragraph: "Klauseln VSG / B 040170 / ** und VSG / B 040171 / **",
    mitErtragsausfall: false,
  },
  {
    key: "unbenannteGefahren",
    ziffer: "9",
    label: "Unbenannte Gefahren",
    paragraph: "§ B4 Nr. 1 j VSG",
    mitErtragsausfall: true,
  },
  {
    key: "glasbruch",
    ziffer: "10",
    label: "Glasbruch (Allgemein- / Gesamtverglasung)",
    paragraph: "§ B4 Nr. 1 h VSG",
    mitErtragsausfall: false,
  },
  {
    key: "transportWerkverkehr",
    ziffer: "11",
    label: "Transportgefahren — Werkverkehr",
    paragraph: "Klausel VSG / B 040172 / 14",
    mitErtragsausfall: false,
  },
  {
    key: "infektionsschutz",
    ziffer: "12",
    label: "Infektionsschutz",
    paragraph: "§ B4 Nr. 1 i VSG",
    mitErtragsausfall: true,
  },
  {
    key: "kuehlgut",
    ziffer: "13",
    label: "Kühlgut / Tiefkühlgut / Medikamentenverderb",
    paragraph: "Klausel VSG / B 040173 / 14",
    mitErtragsausfall: false,
  },
];

export type GefahrRowState = {
  versichert: boolean;
  hoechstentschaedigung: string;
  jahreshoechstentschaedigung: string;
  selbstbeteiligung: string;
  /** Nur für Inhaltsversicherung relevant — ob für diese Gefahr zusätzlich
   *  Ertragsausfall mitversichert ist. */
  ertragsausfall: boolean;
};

export type GefahrenMatrixState = Record<string, GefahrRowState>;

export const emptyMatrixState = (): GefahrenMatrixState => {
  const out: GefahrenMatrixState = {};
  for (const g of INHALT_GEFAHREN) {
    out[g.key] = {
      versichert: false,
      hoechstentschaedigung: "",
      jahreshoechstentschaedigung: "",
      selbstbeteiligung: "",
      ertragsausfall: false,
    };
  }
  return out;
};

/** Vertragsarten, die den Inhalt-/Ertragsausfall-Matrix-Modus nutzen.
 *  Werte werden gegen den im NewContractDialog gewählten Vertragsart-Namen
 *  geprüft (case-insensitive Substring-Match). */
export const INHALT_MATRIX_ARTS = [
  "inhaltsversicherung",
  "ertragsausfall",
] as const;

export const isInhaltOnly = (artName: string | null | undefined): boolean =>
  !!artName && artName.toLowerCase().includes("inhalt");

export const isErtragsausfallOnly = (artName: string | null | undefined): boolean =>
  !!artName && artName.toLowerCase().includes("ertragsausfall");

export const isMatrixMode = (artName: string | null | undefined): boolean =>
  isInhaltOnly(artName) || isErtragsausfallOnly(artName);
